import React, {useCallback} from 'react'
import TableHeader from '../../../../components/table/TableHeader'
import Text from '../../../../components/text/Text'
import TableLayout from '../../../../components/table/TableLayout'
import TableItem from '../../../../components/table/TableItem'
import useNavigation from '../../../../hooks/useNavigation'
import {useTime} from '../../../../utils/time'
import Span from '../../../../components/text/Span'
import useQueryFloatings from '../../../../hooks/api/floating/useQueryFloating'

interface IProps {
    className?: string
    active?: boolean
}

interface IFloatingMessage {
    id: string
    start_at: number
    end_at: number
    priority: number
    language?: string
    text: {
        ko: string
        en: string
        emoji: string
    }
    action_url: {
        web: string
        app: string
    }
    buttonText: {
        ko: string
        en: string
    }
    button_url: {
        web: string
        app: string
    }
}

const FloatingMessageTable: React.FC<IProps> = ({className, active}) => {
    const {goFloatingMessageDetail} = useNavigation()
    const {localTimeString} = useTime()
    const {data: eventList, refetch} = useQueryFloatings(active)

    const getProgressCheckStyle = useCallback((start_at, end_at) => {
        const now = new Date().getTime() / 1000
        if (now < start_at) {
            return (
                <div className={'bg-alt_blue/80 px-[8px] py-[3px] rounded-[5px] text-center w-fit mx-auto'}>
                    <Text className={'text-white text-ti4'}>Soon</Text>
                </div>
            )
        }
        if (now > start_at && now < end_at) {
            return (
                <div className={'bg-primary px-[8px] py-[3px] rounded-[5px] text-center w-fit mx-auto'}>
                    <Text className={'text-white text-ti4'}>Progress</Text>
                </div>
            )
        }
        if (now > end_at) {
            return (
                <div className={'bg-font04 px-[8px] py-[3px] rounded-[5px] text-center w-fit mx-auto'}>
                    <Text className={'text-font02 text-ti4'}>Ended</Text>
                </div>
            )
        }
    }, [])
    return (
        <div className={`${className}`}>
            <TableHeader className={'grid-cols-[1fr_250px_250px_60px_100px_200px] gap-x-[10px] md:hidden'}>
                <Text>Text</Text>
                <Text className={'text-center'}>Filter</Text>
                <Text className={'text-center'}>View</Text>
                <Text className={'text-center'}>Priority</Text>
                <Text className={'text-center'}>Language</Text>
                <Text className={'text-center'}>Date</Text>
            </TableHeader>
            <TableLayout>
                {eventList
                    ?.sort((a, b) => b?.priority - a?.priority)
                    ?.map((item, i) => (
                        <TableItem
                            className={
                                'grid-cols-[1fr_250px_250px_60px_100px_200px] gap-x-[10px] md:grid-cols-[1fr_150px_200px] md:justify-center md:gap-y-[30px] md:space-y-0 md:items-center sm:block sm:space-y-[30px]'
                            }
                            onClick={() => goFloatingMessageDetail(item?.id)}>
                            <div className={'flex flex-col gap-y-[5px] sm:text-center'}>
                                <Text className={'text-ti1 break-all pb-[10px]'}>{item?.title}</Text>
                                {item?.language !== 'en' && (
                                    <Text className={'text-ti1 text-font01 break-all'}>
                                        KO: {item?.emoji}
                                        {item?.text?.ko}
                                    </Text>
                                )}
                                {item?.language !== 'ko' && (
                                    <Text className={'text-ti1 text-font01 break-all'}>
                                        EN: {item?.emoji}
                                        {item?.text?.en}
                                    </Text>
                                )}
                            </div>
                            <div className={'text-center space-y-[5px]'}>
                                {item?.filter ? (
                                    <>
                                        <Text className={'text-ti1'}>{Object?.keys(item?.filter)}</Text>
                                        <Text className={'text-body2'}>
                                            {Object?.values(item?.filter)}{' '}
                                            {Object?.keys(item?.filter)?.at(0)?.includes('balance') ? 'USD' : ''}
                                        </Text>
                                    </>
                                ) : null}
                            </div>
                            <div className={'text-center flex flex-col gap-y-[5px]'}>
                                <div>
                                    <Text className={'text-ti3 text-font03'}>WEB</Text>
                                    <Text className={'text-ti1 text-font01 break-all'}>{item?.action_url?.web}</Text>
                                </div>
                                <div>
                                    <Text className={'text-ti3 text-font03'}>APP</Text>
                                    <Text className={'text-ti1 text-font01 break-all'}>{item?.action_url?.app}</Text>
                                </div>
                            </div>
                            <div className={'md:hidden'}>
                                {getProgressCheckStyle(item?.start_at, item?.end_at)}
                                <Text
                                    className={`text-center ${
                                        item?.priority > 0 ? 'text-font01' : 'text-font03'
                                    } text-ti1`}>
                                    {item?.priority || 0}
                                </Text>
                            </div>
                            <div className={'md:hidden'}>
                                <Text className={'text-center text-ti1 uppercase'}>
                                    {item?.language ? item?.language : '-'}
                                </Text>
                            </div>
                            <div className={'text-right sm:text-center'}>
                                <Text className={`text-body3 text-font03 sm:text-body2`}>
                                    Start At:{' '}
                                    <Span
                                        className={`text-ti3  ${
                                            new Date()?.getTime() > item?.start_at ? 'text-primary' : 'text-font03'
                                        } sm:text-ti2`}>
                                        {localTimeString(item?.start_at)}
                                    </Span>
                                </Text>
                                <Text className={`text-body3 text-font03 sm:text-body2`}>
                                    End At:{' '}
                                    <Span
                                        className={`text-ti3  ${
                                            new Date()?.getTime() > item?.end_at ? 'text-font_red' : 'text-font03'
                                        } sm:text-ti2`}>
                                        {localTimeString(item?.end_at)}
                                    </Span>
                                </Text>
                            </div>
                        </TableItem>
                    ))}
            </TableLayout>
        </div>
    )
}

export default FloatingMessageTable
